/**
 * Catálogo semilla de permisos del sistema.
 *
 * Estas definiciones se usan para poblar `/permissionDefinitions` la primera
 * vez (`scripts/_seed-permissions.mjs`). A partir de allí, la fuente de
 * verdad son los documentos Firestore y esta constante queda como fallback y
 * como referencia visual para el equipo.
 *
 * Cambios comunes:
 * - Añadir un permiso: agregarlo aquí y re-ejecutar el seed (upsert-only).
 * - Retirar un permiso: NO borrar del array; marcar `enabled: false` en el
 *   documento Firestore desde la UI de admin.
 */
import type {
  MemberRole,
  PermissionAction,
  PermissionDefinition,
} from "../../types";

export interface PermissionSeed {
  id: string; // "{module}.{action}"
  module: string;
  action: PermissionAction;
  description: string;
  defaultRoles: MemberRole[];
}

/**
 * Módulos habilitados. Sirven para agrupar en la UI de admin y para
 * validar que un permiso pertenece a un módulo conocido.
 */
export const PERMISSION_MODULES = [
  "members", // Miembros y personas
  "roles", // Cargos y asignaciones
  "events", // Eventos y actividades
  "attendance", // Asistencia
  "finance", // Tesorería
  "progress", // Especialidades / clases / investiduras
  "reports", // Reportes / exportables
  "club", // Configuración del club
  "organization", // Asociación / iglesia / unión (solo admins altos)
] as const;

export type PermissionModule = (typeof PERMISSION_MODULES)[number];

export const PERMISSION_MODULE_LABELS: Record<PermissionModule, string> = {
  members: "Miembros",
  roles: "Cargos",
  events: "Eventos",
  attendance: "Asistencia",
  finance: "Tesorería",
  progress: "Progreso",
  reports: "Reportes",
  club: "Club",
  organization: "Organización",
};

/**
 * Semilla del catálogo de permisos.
 *
 * Convención de nombres: `{module}.{action}`.
 * Cuando un permiso es "escritura amplia", usar `.write`. Cuando es una
 * acción concreta que necesita gating aparte (aprobar, exportar, borrar),
 * agregar entrada dedicada.
 */
export const PERMISSION_SEEDS: PermissionSeed[] = [
  // --- Miembros ---
  {
    id: "members.read",
    module: "members",
    action: "read",
    description: "Ver la lista de miembros y sus fichas.",
    defaultRoles: [
      "director",
      "subdirector",
      "consejero",
      "instructor",
      "tesorero",
      "secretario",
    ],
  },
  {
    id: "members.write",
    module: "members",
    action: "write",
    description: "Crear o editar fichas de miembros.",
    defaultRoles: ["director", "subdirector", "secretario"],
  },
  {
    id: "members.delete",
    module: "members",
    action: "delete",
    description: "Retirar o eliminar miembros.",
    defaultRoles: ["director"],
  },
  {
    id: "members.export",
    module: "members",
    action: "export",
    description: "Exportar listado de miembros.",
    defaultRoles: ["director", "subdirector", "secretario"],
  },

  // --- Cargos ---
  {
    id: "roles.read",
    module: "roles",
    action: "read",
    description: "Ver quién ocupa cada cargo en el club.",
    defaultRoles: [
      "director",
      "subdirector",
      "consejero",
      "instructor",
      "tesorero",
      "secretario",
    ],
  },
  {
    id: "roles.write",
    module: "roles",
    action: "write",
    description: "Asignar o cambiar cargos de las personas del club.",
    defaultRoles: ["director"],
  },

  // --- Eventos ---
  {
    id: "events.read",
    module: "events",
    action: "read",
    description: "Ver eventos y actividades del club.",
    defaultRoles: [
      "director",
      "subdirector",
      "consejero",
      "instructor",
      "tesorero",
      "secretario",
    ],
  },
  {
    id: "events.write",
    module: "events",
    action: "write",
    description: "Crear y editar eventos y actividades.",
    defaultRoles: ["director", "subdirector", "secretario"],
  },
  {
    id: "events.approve",
    module: "events",
    action: "approve",
    description: "Aprobar cambios propuestos y publicar eventos.",
    defaultRoles: ["director"],
  },

  // --- Asistencia ---
  {
    id: "attendance.read",
    module: "attendance",
    action: "read",
    description: "Consultar registros de asistencia.",
    defaultRoles: [
      "director",
      "subdirector",
      "consejero",
      "instructor",
      "secretario",
    ],
  },
  {
    id: "attendance.write",
    module: "attendance",
    action: "write",
    description: "Registrar o modificar asistencia.",
    defaultRoles: [
      "director",
      "subdirector",
      "consejero",
      "instructor",
      "secretario",
    ],
  },

  // --- Tesorería ---
  {
    id: "finance.read",
    module: "finance",
    action: "read",
    description: "Ver movimientos, deudas y estado financiero.",
    defaultRoles: ["director", "tesorero"],
  },
  {
    id: "finance.write",
    module: "finance",
    action: "write",
    description: "Registrar pagos y ajustes financieros.",
    defaultRoles: ["director", "tesorero"],
  },
  {
    id: "finance.approve",
    module: "finance",
    action: "approve",
    description: "Aprobar egresos y conciliaciones.",
    defaultRoles: ["director", "tesorero"],
  },
  {
    id: "finance.export",
    module: "finance",
    action: "export",
    description: "Exportar reportes financieros.",
    defaultRoles: ["director", "tesorero"],
  },

  // --- Progreso ---
  {
    id: "progress.read",
    module: "progress",
    action: "read",
    description: "Ver progreso de clases y especialidades.",
    defaultRoles: [
      "director",
      "subdirector",
      "consejero",
      "instructor",
      "secretario",
    ],
  },
  {
    id: "progress.write",
    module: "progress",
    action: "write",
    description: "Registrar avances de clases y especialidades.",
    defaultRoles: ["director", "consejero", "instructor"],
  },
  {
    id: "progress.approve",
    module: "progress",
    action: "approve",
    description: "Aprobar investiduras y logros.",
    defaultRoles: ["director"],
  },

  // --- Reportes ---
  {
    id: "reports.read",
    module: "reports",
    action: "read",
    description: "Ver reportes agregados del club.",
    defaultRoles: ["director", "subdirector", "tesorero", "secretario"],
  },
  {
    id: "reports.export",
    module: "reports",
    action: "export",
    description: "Exportar reportes.",
    defaultRoles: ["director", "subdirector", "tesorero", "secretario"],
  },

  // --- Configuración del club ---
  {
    id: "club.read",
    module: "club",
    action: "read",
    description: "Ver configuración general del club.",
    defaultRoles: [
      "director",
      "subdirector",
      "consejero",
      "instructor",
      "tesorero",
      "secretario",
    ],
  },
  {
    id: "club.write",
    module: "club",
    action: "write",
    description: "Editar la configuración del club (secciones, unidades).",
    defaultRoles: ["director"],
  },
  {
    id: "club.admin",
    module: "club",
    action: "admin",
    description:
      "Administración plena del club (asignar director, transferir).",
    defaultRoles: ["director"],
  },

  // --- Organización (Asociación / Iglesia) ---
  //
  // Estos permisos se aprovechan sólo para roles altos (admin_association,
  // admin_union) que NO viven en `/rolePolicies` con `role` de miembro. Se
  // dejan aquí para poder auditar el catálogo completo desde una sola vista.
  {
    id: "organization.read",
    module: "organization",
    action: "read",
    description: "Ver estructura de asociaciones e iglesias.",
    defaultRoles: [],
  },
  {
    id: "organization.write",
    module: "organization",
    action: "write",
    description: "Editar estructura de asociaciones e iglesias.",
    defaultRoles: [],
  },
];

/** Set precomputado de ids del catálogo — útil para validaciones rápidas. */
export const PERMISSION_IDS: ReadonlySet<string> = new Set(
  PERMISSION_SEEDS.map((p) => p.id),
);

/**
 * Convierte un `PermissionSeed` en un objeto listo para escribir en Firestore
 * (sin los campos de auditoría, que agrega la capa de servicio).
 */
export function toPermissionDoc(
  seed: PermissionSeed,
): Omit<
  PermissionDefinition,
  "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
> {
  return {
    id: seed.id,
    module: seed.module,
    action: seed.action,
    description: seed.description,
    defaultRoles: seed.defaultRoles,
    enabled: true,
  };
}
